import { theme, useResponsive } from "@product/scmp-sdk";
import { useAtom } from "jotai";
import last from "lodash/last";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { useTakeoverBottomAdSlot } from "~/components/advertisement/takeover-ad-slot/hooks";
import { currentStyleCardCarouselWidgetSectionsAtom } from "~/components/section/section-style/section-style-widgets/story-card-carousel-widget/atoms";
import { StoryCardCarouselItem } from "~/components/section/section-style/section-style-widgets/story-card-carousel-widget/story-card-carousel-item";
import { getUniqueArticlesPerSection } from "~/components/story-style/helpers";
import { useAcquisitionConfig } from "~/lib/app/hooks";
import type { storyCardCarouselWidgetQuery } from "~/queries/__generated__/storyCardCarouselWidgetQuery.graphql";

import {
  CarouselContainer,
  ItemContainer,
  StoryCardCarouselDisplayContainer,
  StyledStyleCardCarouselLabelIcon,
} from "./styles";

export const StoryCardCarouselWidget: FunctionComponent = () => {
  const content = useLazyLoadQuery<storyCardCarouselWidgetQuery>(
    graphql`
      query storyCardCarouselWidgetQuery {
        ...helpersStoryStyleArticleListQuery
      }
    `,
    { fetchPolicy: "store-or-network" },
  );

  const { acquisitionConfigState } = useAcquisitionConfig();
  const isMobile = useResponsive(theme.breakpoints.only("mobile"), true);
  const { isEnableTakeoverBottomAdSlot } = useTakeoverBottomAdSlot();

  const [sections] = useAtom(currentStyleCardCarouselWidgetSectionsAtom);
  const currentSectionEntityId = last(sections)?.entityId;

  const uniqueArticlesPerSection = useMemo(() => {
    if (!currentSectionEntityId) return [];

    return getUniqueArticlesPerSection({
      currentSectionEntityId,
      reference: content,
    });
  }, [currentSectionEntityId, content]);

  if (
    isEnableTakeoverBottomAdSlot ||
    !uniqueArticlesPerSection ||
    !isMobile ||
    !acquisitionConfigState?.result?.styleStoryCarousel.display
  )
    return null;

  // Render fist article of each section
  const handleRenderItems = () =>
    uniqueArticlesPerSection.map(({ articles, section }) =>
      articles.map(
        (article, index) =>
          index === 0 && (
            <ItemContainer key={article.entityId}>
              <StoryCardCarouselItem
                pathname={section.pathname}
                reference={article}
                sectionName={section.name}
              />
            </ItemContainer>
          ),
      ),
    );

  return (
    <StoryCardCarouselDisplayContainer>
      <StyledStyleCardCarouselLabelIcon />
      <CarouselContainer>{handleRenderItems()}</CarouselContainer>
    </StoryCardCarouselDisplayContainer>
  );
};

StoryCardCarouselWidget.displayName = "StoryCardCarouselWidget";
